/**
 * Waveform Audio Player Component
 * 
 * Uses wavesurfer.js to render an audio waveform visualization
 * with play/pause controls and download functionality.
 */
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Box, IconButton, Typography, Slider, Tooltip, CircularProgress, TextField, useTheme, alpha } from '@mui/material';
import {
  PlayArrow,
  Pause,
  Download,
  VolumeUp,
  VolumeOff,
  Speed,
  Check,
  Edit,
  Close,
} from '@mui/icons-material';
import WaveSurfer from 'wavesurfer.js';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../hooks/useNumberFormat';

export interface WaveformSeekRequest {
  time: number;
  play?: boolean;
  requestId: number;
}

interface WaveformPlayerProps {
  url: string;
  displayName?: string;
  downloadFilename?: string;
  displayNameEditable?: boolean;
  displayNameSaving?: boolean;
  durationSeconds?: number | null;
  onDisplayNameCommit?: (nextDisplayName: string) => void | Promise<void>;
  onTimeUpdate?: (time: number) => void;
  onDuration?: (duration: number) => void;
  seekRequest?: WaveformSeekRequest | null;
}

const playbackSpeedOptions = [1, 1.5, 2];

export default function WaveformPlayer({
  url,
  displayName,
  downloadFilename,
  displayNameEditable = false,
  displayNameSaving = false,
  durationSeconds = null,
  onDisplayNameCommit,
  onTimeUpdate,
  onDuration,
  seekRequest,
}: WaveformPlayerProps) {
  const { t } = useTranslation(['components', 'common']);
  const { formatDuration, formatDecimal, formatNumber } = useNumberFormat();
  const containerRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const onTimeUpdateRef = useRef<((time: number) => void) | undefined>(undefined);
  const onDurationRef = useRef<((duration: number) => void) | undefined>(undefined);
  const volumeRef = useRef(1);
  const seekRequestRef = useRef(seekRequest);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isEditingDisplayName, setIsEditingDisplayName] = useState(false);
  const [displayNameDraft, setDisplayNameDraft] = useState(() => String(displayName || '').trim());
  volumeRef.current = volume;
  seekRequestRef.current = seekRequest;
  const seekRequestId = seekRequest?.requestId;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const waveColor = isDarkMode ? alpha(theme.palette.primary.light, 0.6) : theme.palette.grey[400];
  const progressColor = theme.palette.primary.main;
  const cursorColor = alpha(theme.palette.primary.main, 0.75);
  const formatRateLabel = (rate: number) => {
    const label = rate % 1 === 0 ? formatNumber(rate) : formatDecimal(rate, 1);
    return `${label}x`;
  };
  const cyclePlaybackRate = () => {
    const currentIndex = playbackSpeedOptions.findIndex((rate) => rate === playbackRate);
    const nextIndex = (currentIndex + 1) % playbackSpeedOptions.length;
    setPlaybackRate(playbackSpeedOptions[nextIndex]);
  };

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);

  useEffect(() => {
    onDurationRef.current = onDuration;
  }, [onDuration]);

  useEffect(() => {
    setDisplayNameDraft(String(displayName || '').trim());
  }, [displayName]);

  useEffect(() => {
    if (!displayNameEditable || !onDisplayNameCommit) {
      setIsEditingDisplayName(false);
    }
  }, [displayNameEditable, onDisplayNameCommit]);

  useEffect(() => {
    if (!containerRef.current || !url) return;

    setIsLoading(true);
    setHasError(false);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor,
      progressColor,
      cursorColor,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      height: 60,
      normalize: true,
      backend: 'MediaElement',
      cursorWidth: 1,
    });

    wavesurferRef.current = wavesurfer;
    wavesurfer.setVolume(volumeRef.current);

    let isDestroyed = false;
    const controller = new AbortController();

    const handleTimeUpdate = () => {
      if (isDestroyed) return;
      const time = wavesurfer.getCurrentTime();
      setCurrentTime(time);
      onTimeUpdateRef.current?.(time);
    };

    wavesurfer.on('ready', () => {
      if (isDestroyed) return;
      setIsLoading(false);
      const totalDuration = wavesurfer.getDuration();
      setDuration(totalDuration);
      onDurationRef.current?.(totalDuration);
    });
    wavesurfer.on('audioprocess', handleTimeUpdate);
    wavesurfer.on('timeupdate', handleTimeUpdate);
    wavesurfer.on('play', () => {
      if (isDestroyed) return;
      setIsPlaying(true);
    });
    wavesurfer.on('pause', () => {
      if (isDestroyed) return;
      setIsPlaying(false);
    });
    wavesurfer.on('finish', () => {
      if (isDestroyed) return;
      setIsPlaying(false);
    });
    wavesurfer.on('error', (err) => {
      if (isDestroyed) return;
      const errName =
        typeof err === 'object' && err !== null && 'name' in err
          ? (err as { name?: string }).name
          : undefined;
      if (errName === 'AbortError') {
        return;
      }
      console.error('WaveSurfer error:', err);
      setIsLoading(false);
      setHasError(true);
    });

    const loadAudio = async () => {
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          throw new Error('Failed to load recording');
        }
        const blob = await response.blob();
        if (isDestroyed) return;
        wavesurfer.loadBlob(blob);
      } catch (error) {
        const errorName =
          error instanceof Error
            ? error.name
            : typeof error === 'object' && error !== null && 'name' in error
            ? (error as { name?: string }).name
            : undefined;
        if (errorName === 'AbortError') return;
        if (isDestroyed) return;
        console.error('WaveSurfer load error:', error);
        setIsLoading(false);
        setHasError(true);
      }
    };

    loadAudio();

    return () => {
      isDestroyed = true;
      controller.abort();
      wavesurfer.stop();
      wavesurfer.destroy();
      wavesurferRef.current = null;
    };
  }, [cursorColor, progressColor, url, waveColor]);

  useEffect(() => {
    const currentSeekRequest = seekRequestRef.current;
    if (!currentSeekRequest || !wavesurferRef.current) return;
    const wavesurfer = wavesurferRef.current;
    const seekable = wavesurfer as WaveSurfer & { setTime?: (time: number) => void };
    const totalDuration = duration || wavesurfer.getDuration();
    if (!Number.isFinite(totalDuration) || totalDuration <= 0) return;

    const clampedTime = Math.min(Math.max(0, currentSeekRequest.time), totalDuration);
    if (typeof seekable.setTime === 'function') {
      seekable.setTime(clampedTime);
    } else {
      wavesurfer.seekTo(clampedTime / totalDuration);
    }
    setCurrentTime(clampedTime);
    onTimeUpdateRef.current?.(clampedTime);
    if (currentSeekRequest.play) {
      wavesurfer.play();
    }
  }, [duration, seekRequestId]);

  useEffect(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.setPlaybackRate(playbackRate);
    }
  }, [playbackRate]);

  const handlePlayPause = useCallback(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  }, []);

  const handleVolumeChange = useCallback((_: Event, newValue: number | number[]) => {
    const vol = newValue as number;
    setVolume(vol);
    setIsMuted(vol === 0);
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(vol);
    }
  }, []);

  const handleMuteToggle = useCallback(() => {
    if (wavesurferRef.current) {
      if (isMuted) {
        wavesurferRef.current.setVolume(volume || 1);
        setIsMuted(false);
      } else {
        wavesurferRef.current.setVolume(0);
        setIsMuted(true);
      }
    }
  }, [isMuted, volume]);

  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = downloadFilename || displayName || 'recording.mp3';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  }, [downloadFilename, displayName, url]);

  const durationLabelValue = useMemo(() => {
    if (Number.isFinite(duration) && duration > 0) {
      return formatDecimal(duration, 2);
    }
    if (Number.isFinite(durationSeconds) && durationSeconds && durationSeconds > 0) {
      return formatDecimal(durationSeconds, 2);
    }
    return null;
  }, [duration, durationSeconds, formatDecimal]);
  const resolvedDisplayName = displayName || t('components:waveformPlayer.unnamed', 'Unnamed audio');
  const canCommitDisplayName = displayNameEditable && typeof onDisplayNameCommit === 'function';
  const displayNameDirty = canCommitDisplayName && resolvedDisplayName !== displayNameDraft.trim();
  const handleDisplayNameCommit = useCallback(async () => {
    if (!canCommitDisplayName || !onDisplayNameCommit) {
      return;
    }
    const trimmedDraft = displayNameDraft.trim();
    if (!trimmedDraft) {
      setDisplayNameDraft(resolvedDisplayName);
      return;
    }
    await onDisplayNameCommit(trimmedDraft);
    setIsEditingDisplayName(false);
  }, [canCommitDisplayName, displayNameDraft, onDisplayNameCommit, resolvedDisplayName]);

  if (hasError) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 2, 
        bgcolor: 'error.light', 
        borderRadius: 1,
        color: 'error.contrastText'
      }}>
        <Typography variant="body2">
          {t('components:callDetails.recordingLoadError', 'Erro ao carregar gravação')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      bgcolor: 'background.default', 
      borderRadius: 2, 
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
    }}>
      {/* Waveform Container */}
      <Box 
        ref={containerRef} 
        sx={{ 
          width: '100%', 
          minHeight: 60,
          position: 'relative',
          display: isLoading ? 'none' : 'block',
        }}
      />
      
      {/* Loading State */}
      {isLoading && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: 60,
          gap: 2
        }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            {t('components:callDetails.loadingRecording', 'Carregando gravação...')}
          </Typography>
        </Box>
      )}
      
      {/* Controls */}
      {!isLoading && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mt: 1.5,
        }}>
          {/* Play/Pause Button */}
          <IconButton 
            onClick={handlePlayPause}
            color="primary"
            sx={{ 
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
          
          {/* Time Display */}
          <Typography variant="body2" sx={{ minWidth: 90, fontFamily: 'monospace' }}>
            {formatDuration(currentTime)} / {formatDuration(duration)}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title={`${t('components:callDetails.toggleSpeed', 'Alternar velocidade')} • ${formatRateLabel(
                playbackRate,
              )}`}
              arrow
            >
              <IconButton
                size="small"
                onClick={cyclePlaybackRate}
                aria-label={`${t('components:callDetails.toggleSpeed', 'Alternar velocidade')} (${formatRateLabel(
                  playbackRate,
                )})`}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: playbackRate === 1 ? 'divider' : 'primary.main',
                  bgcolor:
                    playbackRate === 1
                      ? 'transparent'
                      : alpha(theme.palette.primary.main, 0.12),
                  color: playbackRate === 1 ? 'text.secondary' : 'primary.main',
                }}
              >
                <Speed fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Spacer */}
          <Box sx={{ flex: 1 }} />
          
          {/* Volume Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
            <IconButton size="small" onClick={handleMuteToggle}>
              {isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
            </IconButton>
            <Slider
              size="small"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              min={0}
              max={1}
              step={0.01}
              sx={{ width: 80 }}
            />
          </Box>
          
          {/* Download Button */}
          <Tooltip title={t('components:callDetails.downloadRecording', 'Baixar Gravação')}>
            <IconButton 
              onClick={handleDownload}
              color="primary"
              sx={{
                border: '1px solid',
                borderColor: 'primary.main',
              }}
            >
              <Download />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography variant="caption" color="text.secondary">
          {t('components:waveformPlayer.durationLabel', 'Duration')}: {durationLabelValue ? `${durationLabelValue}s` : '—'}
        </Typography>
        {isEditingDisplayName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label={t('components:waveformPlayer.filenameLabel', 'File name')}
              value={displayNameDraft}
              onChange={(event) => setDisplayNameDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleDisplayNameCommit();
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setDisplayNameDraft(resolvedDisplayName);
                  setIsEditingDisplayName(false);
                }
              }}
              disabled={displayNameSaving}
            />
            <Tooltip title={t('components:waveformPlayer.saveFilename', 'Save file name')} arrow>
              <span>
                <IconButton
                  color="primary"
                  disabled={!displayNameDirty || displayNameSaving}
                  onClick={() => void handleDisplayNameCommit()}
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  {displayNameSaving ? <CircularProgress size={18} color="inherit" /> : <Check />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('components:waveformPlayer.cancelFilenameEdit', 'Cancel editing file name')} arrow>
              <IconButton
                onClick={() => {
                  setDisplayNameDraft(resolvedDisplayName);
                  setIsEditingDisplayName(false);
                }}
                disabled={displayNameSaving}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('components:waveformPlayer.filenameLabel', 'File name')}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                {resolvedDisplayName}
              </Typography>
            </Box>
            {canCommitDisplayName && (
              <Tooltip title={t('components:waveformPlayer.editFilename', 'Edit file name')} arrow>
                <span>
                  <IconButton
                    onClick={() => setIsEditingDisplayName(true)}
                    disabled={displayNameSaving}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {displayNameSaving ? <CircularProgress size={18} color="inherit" /> : <Edit />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
